import React, { useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { unwrapGet } from "../../../../helpers/auditHub_helper";
import { AH_ACCENT, AH_BORDER, AH_MUTED, AH_SUBTLE, AH_TEXT, AH_TRACK, AH_WHITE } from "../theme";

const PAGE_SIZE = 20;

const formatWhen = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return String(iso);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const linkButtonSx = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.8125rem",
  height: 30,
  px: 1.25,
  borderRadius: "8px",
  color: "#1976d2",
  "&:hover": { backgroundColor: "#eff6ff" },
  "&.Mui-disabled": { color: "#94a3b8" },
};

const identity = (value) => value || "";

/**
 * Newest-first event feed for `GET .../events`. `fetchPage({ page, size })` must
 * resolve to a `{ items, total }` page. Changing `resetKey` clears and reloads
 * from page 1; changing `refreshKey` reloads page 1 while keeping the list on screen.
 */
const ActivityTimeline = ({
  fetchPage,
  resetKey,
  refreshKey,
  nameFor = identity,
  showControl = false,
  emptyText = "No activity yet.",
}) => {
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;
  const resetRef = useRef(resetKey);
  resetRef.current = resetKey;
  const loadedKeyRef = useRef(null);

  const [state, setState] = useState({ status: "loading", items: [], total: 0, page: 0, error: null });
  const [reloadToken, setReloadToken] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (loadedKeyRef.current !== resetKey) {
      setState({ status: "loading", items: [], total: 0, page: 0, error: null });
    }
    loadedKeyRef.current = resetKey;
    setMoreError(null);
    Promise.resolve(fetchRef.current({ page: 1, size: PAGE_SIZE }))
      .then((res) => {
        if (cancelled) return;
        const body = unwrapGet(res);
        const items = Array.isArray(body?.items) ? body.items : [];
        setState({ status: "ready", items, total: Number(body?.total) || items.length, page: 1, error: null });
      })
      .catch((err) => {
        if (!cancelled) {
          setState({ status: "error", items: [], total: 0, page: 0, error: err?.message || "Unable to load activity." });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [resetKey, refreshKey, reloadToken]);

  const loadMore = async () => {
    if (loadingMore) return;
    const key = resetRef.current;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const body = unwrapGet(await fetchRef.current({ page: state.page + 1, size: PAGE_SIZE }));
      if (resetRef.current !== key) return;
      const batch = Array.isArray(body?.items) ? body.items : [];
      setState((prev) => {
        const seen = new Set(prev.items.map((item) => item.eventId));
        return {
          ...prev,
          items: [...prev.items, ...batch.filter((item) => !seen.has(item.eventId))],
          total: Number(body?.total) || prev.total,
          page: prev.page + 1,
        };
      });
    } catch (err) {
      if (resetRef.current === key) setMoreError(err?.message || "Unable to load more activity.");
    } finally {
      setLoadingMore(false);
    }
  };

  if (state.status === "loading") {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (state.status === "error") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, py: 1 }}>
        <Typography role="alert" sx={{ fontSize: 13, color: "#b91c1c" }}>
          {state.error}
        </Typography>
        <Button variant="text" onClick={() => setReloadToken((value) => value + 1)} sx={linkButtonSx}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!state.items.length) {
    return <Typography sx={{ fontSize: 13, color: AH_MUTED, py: 1 }}>{emptyText}</Typography>;
  }

  const hasMore = state.items.length < state.total;

  return (
    <Box>
      <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0 }}>
        {state.items.map((item, index) => {
          const last = index === state.items.length - 1;
          const meta = [nameFor(item.actorEmail), formatWhen(item.createdAt)].filter(Boolean).join(" · ");
          return (
            <Box component="li" key={item.eventId} sx={{ display: "flex", gap: 1.5 }}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, width: 12 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    mt: 0.625,
                    borderRadius: "50%",
                    border: `2px solid ${AH_ACCENT}`,
                    backgroundColor: AH_WHITE,
                    flexShrink: 0,
                  }}
                />
                {!last ? <Box sx={{ flex: 1, width: "2px", backgroundColor: AH_BORDER, mt: 0.5 }} /> : null}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, pb: last ? 0 : 2 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: AH_TEXT, lineHeight: 1.5, overflowWrap: "anywhere" }}>
                  {item.title}
                </Typography>
                {showControl && item.controlCode ? (
                  <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75, mt: 0.25 }}>
                    <Box
                      component="span"
                      sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: AH_SUBTLE,
                        backgroundColor: AH_TRACK,
                        border: `1px solid ${AH_BORDER}`,
                        borderRadius: "6px",
                        px: 0.75,
                        lineHeight: 1.6,
                      }}
                    >
                      {item.controlCode}
                    </Box>
                    {item.controlTitle ? (
                      <Typography component="span" sx={{ fontSize: 12, color: AH_SUBTLE, overflowWrap: "anywhere" }}>
                        {item.controlTitle}
                      </Typography>
                    ) : null}
                  </Box>
                ) : null}
                {item.detail ? (
                  <Typography sx={{ fontSize: 12.5, color: "#374151", mt: 0.25, lineHeight: 1.5, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                    {item.detail}
                  </Typography>
                ) : null}
                <Typography sx={{ fontSize: 12, color: AH_MUTED, mt: 0.25, overflowWrap: "anywhere" }}>
                  {meta}
                  {item.noteWithheld ? (
                    <Box component="span" sx={{ fontStyle: "italic" }}>
                      {meta ? " · " : ""}Note withheld
                    </Box>
                  ) : null}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
        <Typography sx={{ fontSize: 12, color: AH_MUTED }}>
          Showing {state.items.length} of {state.total}
        </Typography>
        {hasMore ? (
          <Button
            variant="text"
            disabled={loadingMore}
            onClick={loadMore}
            startIcon={loadingMore ? <CircularProgress size={14} color="inherit" /> : null}
            sx={linkButtonSx}
          >
            {loadingMore ? "Loading..." : "Load more"}
          </Button>
        ) : null}
        {moreError ? (
          <Typography role="alert" sx={{ fontSize: 12, color: "#b91c1c" }}>
            {moreError}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

export default ActivityTimeline;
